import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './Portfolio.module.scss';
import { fetchPortfolioItems, formatPortfolioPrice, usePortfolioCategories } from './portfolioService';
import { PortfolioItem } from './portfolio.types';
import { apiUrl } from '../../services/apiClient';

type ViewMode = 'grid' | 'list';
type SortKey = 'latest' | 'popular' | 'name';

const PAGE_SIZE = 12;

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '최신순' },
  { key: 'popular', label: '인기순' },
  { key: 'name', label: '이름순' },
];

function Portfolio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categories = usePortfolioCategories();
  const categoryFilters = useMemo(() => [{ id: 'all', label: 'ALL' }, ...(categories ?? [])], [categories]);
  // URL(?cat=)을 기준으로 삼아, 이미 /portfolio에 있을 때 헤더 서브메뉴를 눌러도 필터가 바뀌게 한다
  const requestedCategory = searchParams.get('cat') ?? 'all';
  const activeCategory =
    categories && !categories.some((c) => c.id === requestedCategory) ? 'all' : requestedCategory;
  const [view, setView] = useState<ViewMode>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PortfolioItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetchPortfolioItems()
      .then(setItems)
      .catch((err) => setLoadError(err instanceof Error ? err.message : '포트폴리오를 불러오지 못했습니다.'));
  }, []);

  const handleCategoryChange = (id: string) => {
    setSearchParams(id === 'all' ? {} : { cat: id }, { replace: true });
  };

  const filteredItems = useMemo(() => {
    const all = items ?? [];
    const filtered = activeCategory === 'all' ? all : all.filter((item) => item.categoryId === activeCategory);
    const sorted = [...filtered];
    if (sortKey === 'latest') {
      sorted.sort((a, b) => b.date.localeCompare(a.date));
    } else if (sortKey === 'popular') {
      sorted.sort((a, b) => b.popularity - a.popularity);
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [items, activeCategory, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <div className={styles.pageHeadWrap}>
        <section className={styles.pageHead}>
          <div className={styles.crumb}>
            <Link to="/">HOME</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <Link to="/order">주문하기</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className={styles.crumbHere}>디자인 없음</span>
          </div>
          {/* <span className={styles.eyebrow}>Portfolio</span> */}
          <h1 className={styles.pageTitle}>
            Design &amp; <em>Order</em>
          </h1>
          <p className={styles.pageDesc}>
            디자인 파일이 없어도 괜찮습니다. 원하시는 상품을 선택해 주문하시면 G-WORKS가 디자인 시안부터 제작 · 배송까지 함께합니다.
          </p>
        </section>
      </div>

      {/* FILTER BAR */}
      <div className={styles.filterBarWrap}>
        <div className={styles.filterBar}>
          <div className={styles.filters}>
            {categoryFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`${styles.filter} ${activeCategory === filter.id ? styles.active : ''}`}
                onClick={() => handleCategoryChange(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className={styles.filterTools}>
            <select
              className={styles.sortSelect}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`${styles.viewBtn} ${view === 'grid' ? styles.active : ''}`}
              aria-label="그리드 보기"
              onClick={() => setView('grid')}
            >
              <svg viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
            <button
              type="button"
              className={`${styles.viewBtn} ${view === 'list' ? styles.active : ''}`}
              aria-label="리스트 보기"
              onClick={() => setView('list')}
            >
              <svg viewBox="0 0 24 24">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* GRID */}
      <div className={styles.gridWrap}>
        {loadError && <p className={styles.emptyState}>{loadError}</p>}
        {!loadError && items === null && <p className={styles.emptyState}>불러오는 중...</p>}
        {!loadError && items !== null && pagedItems.length === 0 && (
          <p className={styles.emptyState}>해당 카테고리의 작업물이 아직 없습니다.</p>
        )}
        {!loadError && items !== null && pagedItems.length > 0 && (
          <div className={`${styles.grid} ${view === 'list' ? styles.list : ''}`}>
            {pagedItems.map((item, idx) => (
              <Link
                key={item.id}
                to={`/portfolio/${item.id}`}
                className={styles.pCard}
                style={{ '--i': idx } as React.CSSProperties}
              >
                <div className={`${styles.pThumb} ${styles[`tone${item.tone}`]}`}>
                  {item.thumbnailUrl && (
                    <img src={apiUrl(item.thumbnailUrl)} alt={item.name} className={styles.pThumbImg} loading="lazy" />
                  )}
                  <span className={styles.pCode}>{item.code}</span>
                  <span className={styles.pCatBadge}>{item.categoryLabel}</span>
                  {!item.thumbnailUrl && <div className={styles.pThumbDeco}>{item.deco}</div>}
                  <div className={styles.pThumbCta}>
                    VIEW
                    <svg viewBox="0 0 24 24">
                      <path d="M5 12h14" />
                      <path d="m13 6 6 6-6 6" />
                    </svg>
                  </div>
                </div>
                <div className={styles.pMeta}>
                  <span className={styles.pBrand}>{item.brand}</span>
                  <h3 className={styles.pName}>{item.name}</h3>
                  <p className={styles.pTag}>{item.tag}</p>
                  <p className={styles.pPrice}>
                    {formatPortfolioPrice(item)}
                    {item.price !== null && item.priceNote && <span>{item.priceNote}</span>}
                  </p>
                  <div className={styles.pMetaBot}>
                    <ul className={styles.pSpec}>
                      <li>{item.specTag}</li>
                    </ul>
                    <span>{item.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="이전"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <svg viewBox="0 0 24 24">
                <path d="m15 6-6 6 6 6" />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                className={`${styles.pageBtn} ${p === currentPage ? styles.active : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="다음"
              disabled={currentPage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <svg viewBox="0 0 24 24">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Portfolio;
